import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  PANEL_DESCRIPTIONS,
  PANEL_IDS,
  PANEL_TITLES,
  type PanelId,
} from '@trajectory/ui';
import { getCommands } from '@/lib/commands';
import { useDockviewApiStore } from '@/stores/layoutStore';
import { useUiStore } from '@/stores/uiStore';
import { addPanelToDefaultPosition } from './defaultLayout';

interface PaletteItem {
  id: string;
  title: string;
  description: string;
  category: string;
  shortcut?: string;
  run: () => void;
}

function focusPanel(id: PanelId): void {
  const { api } = useDockviewApiStore.getState();
  if (!api) return;
  let panel = api.getPanel(id);
  if (!panel) {
    addPanelToDefaultPosition(api, id);
    panel = api.getPanel(id);
  }
  panel?.api.setActive();
}

/**
 * One dialog, two modes: `'commands'` (Ctrl+K) runs anything in the command
 * registry, `'panels'` (Ctrl+P) jumps to — opening if necessary — a specific
 * panel. Sharing the shell keeps the fuzzy-filter, keyboard nav and styling
 * in one place rather than duplicated across a command palette and a
 * separate search dialog.
 */
export function Palette() {
  const paletteMode = useUiStore((state) => state.paletteMode);
  const closePalette = useUiStore((state) => state.closePalette);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const items: PaletteItem[] = useMemo(() => {
    if (paletteMode === 'panels') {
      return PANEL_IDS.map((id) => ({
        id: `panel.go.${id}`,
        title: PANEL_TITLES[id],
        description: PANEL_DESCRIPTIONS[id],
        category: 'Go to panel',
        run: () => focusPanel(id),
      }));
    }
    return getCommands();
  }, [paletteMode]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      `${item.title} ${item.category} ${item.description}`.toLowerCase().includes(q),
    );
  }, [items, query]);

  useEffect(() => setActiveIndex(0), [filtered.length, paletteMode]);

  useEffect(() => {
    if (paletteMode === 'closed') return;
    setQuery('');
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [paletteMode]);

  const runItem = (item: PaletteItem | undefined) => {
    if (!item) return;
    item.run();
    closePalette();
  };

  return (
    <Dialog
      open={paletteMode !== 'closed'}
      onOpenChange={(open) => {
        if (!open) closePalette();
      }}
      title={paletteMode === 'panels' ? 'Go to panel' : 'Command palette'}
      className="top-[12%] max-w-xl p-0"
    >
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            runItem(filtered[activeIndex]);
          }
        }}
        placeholder={paletteMode === 'panels' ? 'Go to panel…' : 'Type a command…'}
        className="w-full border-b border-border bg-transparent px-4 py-3 text-sm text-text outline-none placeholder:text-faint"
      />
      <ul className="max-h-80 overflow-auto py-1" role="listbox">
        {filtered.length === 0 && <li className="px-4 py-6 text-center text-sm text-faint">No matches</li>}
        {filtered.map((item, index) => (
          <li key={item.id} role="option" aria-selected={index === activeIndex}>
            <button
              type="button"
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => runItem(item)}
              className={`flex w-full items-center justify-between gap-4 px-4 py-2 text-left text-sm ${
                index === activeIndex ? 'bg-surface-sunken text-text' : 'text-muted'
              }`}
            >
              <span className="min-w-0">
                <span className="block">
                  <span className="text-faint">{item.category}: </span>
                  {item.title}
                </span>
                <span className="mt-0.5 block truncate text-xs text-faint">
                  {item.description}
                </span>
              </span>
              {item.shortcut && <span className="text-xs text-faint">{item.shortcut}</span>}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
